import { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';

/**
 * Unified Data Management Hook
 * 
 * Layer 1: Core data management with no context dependencies
 * Handles ALL data types with backend-first confidence scoring
 * 
 * Key Features:
 * - Backend-first with confidence scoring
 * - Smart data merging from multiple sources  
 * - Data source priority: user-entered > api-enriched > estimated
 * - Intelligent fallback when backend unavailable
 * - Single source of truth via backend intelligence
 * - NO circular dependencies with contexts
 * 
 * Persistence Modes:
 * - 'auto': Full bells and whistles with localStorage, confidence scoring, etc.
 * - 'manual': Simple CRUD only - no localStorage, no auto-persistence, straight to API
 */
export const useDataManager = (persistenceMode = 'auto') => {
  
  // Generalized state for all data types
  const [dataStates, setDataStates] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});

  /**
   * Collect all local sources for any data type
   * Backend will determine which has highest confidence
   * No dependencies on contexts - reads directly from localStorage
   */
  const collectLocalSources = useCallback(async (dataType) => {
    const sources = [];

    switch (dataType) {
      case 'assessment':
        // Assessment-specific sources (from original useAssessmentManager)
        const businessCaseCompleted = localStorage.getItem('business-case-completed');
        if (businessCaseCompleted) {
          try {
            const data = JSON.parse(businessCaseCompleted);
            sources.push({
              source: 'business-case-localStorage',
              data: data.formData,
              timestamp: data.timestamp
            });
          } catch (error) {
            console.warn('Failed to parse business case localStorage:', error);
          }
        }

        // Form persistence sources
        const routes = ['/business-case', '/implementation'];
        for (const route of routes) {
          const formData = localStorage.getItem(`motionlogic_form_${route}`);
          if (formData) {
            try {
              const parsed = JSON.parse(formData);
              if (parsed.formData) {
                sources.push({
                  source: `form-persistence-${route}`,
                  data: parsed.formData,
                  timestamp: parsed.timestamp
                });
              }
            } catch (error) {
              console.warn(`Failed to parse form persistence data for ${route}:`, error);
            }
          }
        }

        // Journey context data (read directly from localStorage)
        const journeyContext = localStorage.getItem('motionlogic_journey_context');
        if (journeyContext) {
          try {
            const data = JSON.parse(journeyContext);
            if (data.businessData?.siteInfo) {
              sources.push({
                source: 'journey-context',
                data: data.businessData.siteInfo,
                timestamp: data.timestamp
              });
            }
          } catch (error) {
            console.warn('Failed to parse journey context for assessment:', error);
          }
        }
        break;

      case 'journey':
        // Journey-specific sources
        const journeyContextData = localStorage.getItem('motionlogic_journey_context');
        if (journeyContextData) {
          try {
            const data = JSON.parse(journeyContextData);
            sources.push({
              source: 'journey-localStorage',
              data: data,
              timestamp: data.timestamp
            });
          } catch (error) {
            console.warn('Failed to parse journey context:', error);
          }
        }

        // Analysis history from any source
        const analysisHistoryKey = 'motionlogic_analysis_history';
        const analysisHistory = localStorage.getItem(analysisHistoryKey);
        if (analysisHistory) {
          try {
            const data = JSON.parse(analysisHistory);
            if (data.length > 0) {
              sources.push({
                source: 'journey-context-analysis',
                data: { analysisHistory: data },
                timestamp: null
              });
            }
          } catch (error) {
            console.warn('Failed to parse analysis history:', error);
          }
        }
        break;

      case 'sites':
        // Site-specific sources
        const currentUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
        if (currentUser.id) {
          const userSites = localStorage.getItem(`user_sites_${currentUser.id}`);
          if (userSites) {
            try {
              const data = JSON.parse(userSites);
              sources.push({
                source: 'sites-localStorage',
                data: data,
                timestamp: null
              });
            } catch (error) {
              console.warn('Failed to parse user sites:', error);
            }
          }
        }
        break;

      default:
        console.warn(`Unknown data type: ${dataType}`);
    }

    return sources;
  }, []); // No dependencies on contexts - reads directly from localStorage

  /**
   * Local fallback only when backend is completely unavailable
   * This is a degraded mode, not the normal operation
   */
  const getLocalFallback = useCallback(async (dataType) => {
    console.warn(`Using local fallback for ${dataType} - backend unavailable`);
    
    const sources = await collectLocalSources(dataType);
    
    if (sources.length === 0) {
      return {
        exists: false,
        source: null,
        confidence: null,
        data: null,
        lastUpdated: null,
        id: null,
        degradedMode: true
      };
    }

    // In fallback mode, frontend makes best guess
    // But this is clearly marked as degraded functionality
    const bestSource = sources.reduce((best, current) => {
      const bestTime = new Date(best.timestamp || 0).getTime();
      const currentTime = new Date(current.timestamp || 0).getTime();
      return currentTime > bestTime ? current : best;
    });

    return {
      exists: true,
      source: `${bestSource.source}-fallback`,
      confidence: 0.3, // Low confidence - no backend validation
      data: bestSource.data,
      lastUpdated: bestSource.timestamp,
      id: null,
      degradedMode: true
    };
  }, [collectLocalSources]);

  /**
   * Check if data exists across all possible sources
   * Backend handles all source comparison and confidence scoring
   */
  const checkUnifiedDataStatus = useCallback(async (dataType) => {
    setLoading(prev => ({ ...prev, [dataType]: true }));
    setError(prev => ({ ...prev, [dataType]: null }));

    try {
      let response = null;
      
      // Route to appropriate backend endpoint based on dataType
      switch (dataType) {
        case 'assessment':
          // Check for existing site assessments (drafts and completed)
          try {
            const assessments = await apiClient.siteAssessment.getList();
            if (assessments && assessments.length > 0) {
              // Find most recent draft or completed assessment
              const mostRecent = assessments.reduce((latest, current) => {
                const latestTime = new Date(latest.created_at || latest.last_updated || 0).getTime();
                const currentTime = new Date(current.created_at || current.last_updated || 0).getTime();
                return currentTime > latestTime ? current : latest;
              });
              
              response = {
                exists: true,
                source: 'backend',
                confidence: 1.0,
                data: mostRecent,
                lastUpdated: mostRecent.last_updated || mostRecent.created_at,
                id: mostRecent.id,
                migrated: false
              };
            } else {
              response = {
                exists: false,
                source: null,
                confidence: null,
                data: null,
                lastUpdated: null,
                id: null,
                migrated: false
              };
            }
          } catch (backendErr) {
            throw backendErr; // Fall through to local fallback
          }
          break;
          
        case 'journey':
          // Check for existing customer journeys
          try {
            const journeys = await apiClient.journey.getList();
            if (journeys && journeys.length > 0) {
              const mostRecent = journeys[journeys.length - 1]; // Assuming array is ordered
              response = {
                exists: true,
                source: 'backend',
                confidence: 1.0,
                data: mostRecent,
                lastUpdated: mostRecent.last_updated || mostRecent.created_at,
                id: mostRecent.id,
                migrated: false
              };
            } else {
              response = {
                exists: false,
                source: null,
                confidence: null,
                data: null,
                lastUpdated: null,
                id: null,
                migrated: false
              };
            }
          } catch (backendErr) {
            throw backendErr; // Fall through to local fallback
          }
          break;
          
        case 'sites':
          // For sites data, check localStorage directly since it's client-side only
          const localSources = await collectLocalSources(dataType);
          if (localSources.length > 0) {
            const mostRecent = localSources.reduce((latest, current) => {
              const latestTime = new Date(latest.timestamp || 0).getTime();
              const currentTime = new Date(current.timestamp || 0).getTime();
              return currentTime > latestTime ? current : latest;
            });
            
            response = {
              exists: true,
              source: 'localStorage',
              confidence: 0.8,
              data: mostRecent.data,
              lastUpdated: mostRecent.timestamp,
              id: null,
              migrated: false
            };
          } else {
            response = {
              exists: false,
              source: null,
              confidence: null,
              data: null,
              lastUpdated: null,
              id: null,
              migrated: false
            };
          }
          break;
          
        default:
          throw new Error(`Unknown dataType: ${dataType}`);
      }
      
      // Update state for this data type
      setDataStates(prev => ({
        ...prev,
        [dataType]: {
          exists: response.exists,
          source: response.source,
          confidence: response.confidence,
          data: response.data,
          lastUpdated: response.lastUpdated,
          id: response.id,
          degradedMode: false
        }
      }));

      return response;
    } catch (err) {
      console.warn(`Backend unavailable for ${dataType}, using local fallback:`, err);
      
      // Only use local fallback if backend is completely unavailable
      const localStatus = await getLocalFallback(dataType);
      setDataStates(prev => ({
        ...prev,
        [dataType]: localStatus
      }));
      return localStatus;
    } finally {
      setLoading(prev => ({ ...prev, [dataType]: false }));
    }
  }, [collectLocalSources, getLocalFallback]);

  /**
   * Get data with confidence indicators
   * Returns data formatted for form pre-population
   */
  const getDataWithConfidence = useCallback((dataType) => {
    const dataState = dataStates[dataType];
    if (!dataState?.exists) return null;

    const data = dataState.data;
    const source = dataState.source;
    const confidence = dataState.confidence;

    // Add confidence metadata to each field
    const enrichedData = {};
    
    Object.keys(data).forEach(key => {
      enrichedData[key] = {
        value: data[key],
        source: source,
        confidence: calculateFieldConfidence(key, data[key], source),
        editable: true
      };
    });

    return {
      formData: data, // Raw data for forms
      enrichedData, // Data with confidence metadata
      metadata: {
        source,
        confidence,
        lastUpdated: dataState.lastUpdated,
        dataId: dataState.id,
        degradedMode: dataState.degradedMode
      }
    };
  }, [dataStates]);

  /**
   * Calculate confidence score for individual fields
   * Priority: user-entered > api-enriched > estimated
   */
  const calculateFieldConfidence = (fieldName, value, source) => {
    if (!value) return 0;

    // User-entered data has highest confidence
    if (source === 'business-case' || source === 'implementation' || source === 'business-case-localStorage') {
      return 0.9;
    }

    // Database data has high confidence
    if (source === 'database-draft' || source.includes('form-persistence')) {
      return 0.8;
    }

    // API enrichment has medium confidence
    if (source === 'api-enrichment') {
      return 0.7;
    }

    // Estimated or derived data has lower confidence
    if (source === 'estimated' || source === 'journey-context') {
      return 0.5;
    }

    return 0.3; // Unknown source
  };

  /**
   * Create or update unified data
   * Routes to appropriate backend endpoint based on dataType
   * 
   * In manual mode: Simple direct CRUD to API, no localStorage bells and whistles
   * In auto mode: Full confidence scoring, localStorage, smart merging
   */
  const saveUnifiedData = useCallback(async (dataType, newData, source = 'user-direct', mergeExisting = true) => {
    setLoading(prev => ({ ...prev, [dataType]: true }));
    setError(prev => ({ ...prev, [dataType]: null }));

    try {
      let response;
      
      if (persistenceMode === 'manual') {
        // Simple CRUD mode - direct to API, no localStorage complexity
        switch (dataType) {
          case 'assessment':
            // Check if we have an existing assessment ID to update
            const existingAssessmentId = dataStates.assessment?.id;
            
            try {
              if (existingAssessmentId) {
                // Update existing assessment
                console.log(`[useDataManager] Updating existing assessment ${existingAssessmentId}`);
                response = await apiClient.siteAssessment.update(existingAssessmentId, newData, {
                  source: source,
                  merge_strategy: 'replace' // Simple replace in manual mode
                });
                console.log(`[useDataManager] Update response:`, response?.data?.id || response?.id);
              } else {
                // Create new assessment
                console.log('[useDataManager] Creating new assessment');
                response = await apiClient.siteAssessment.create(newData, {
                  source: source,
                  merge_existing: false // Keep it simple - no merging in manual mode
                });
                console.log(`[useDataManager] Create response:`, response?.data?.id || response?.id);
              }
            } catch (error) {
              if (error.response?.status === 422 && error.response?.data?.detail) {
                // Handle validation errors cleanly
                throw new Error(`Validation failed: ${JSON.stringify(error.response.data.detail)}`);
              }
              throw error;
            }
            break;
            
          case 'journey':
            response = await apiClient.journey.createOrUpdate({
              dataType,
              data: newData,
              source: source
            });
            break;
            
          default:
            throw new Error(`Unsupported data type in manual mode: ${dataType}`);
        }
      } else {
        // Auto mode - full bells and whistles (existing logic)
        // Route to appropriate backend endpoint based on data type
        switch (dataType) {
          case 'assessment':
            // Use the confidence-based site assessment endpoint
            try {
              response = await apiClient.siteAssessment.create(newData, {
                source: source,
                merge_existing: mergeExisting
              });
            } catch (createError) {
              // Fallback: try to update existing assessment
              console.warn('Create failed, attempting to find and update existing assessment:', createError);
              
              // Get list of assessments to find existing one
              const assessments = await apiClient.siteAssessment.getList();
              if (assessments && assessments.length > 0) {
                // Find most recent draft or pending assessment
                const existing = assessments.find(a => 
                  a.status === 'DRAFT' || a.status === 'PENDING'
                ) || assessments[0];
                
                if (existing) {
                  response = await apiClient.siteAssessment.update(existing.id, newData, {
                    source: source,
                    merge_strategy: 'confidence'
                  });
                } else {
                  throw createError; // Re-throw original error
                }
              } else {
                throw createError; // Re-throw original error
              }
            }
            break;
          
            break;
            
          case 'journey':
            response = await apiClient.journey.createOrUpdate({
              dataType,
              data: newData,
              source: source
            });
            break;
            
          case 'sites':
            // For now, store in localStorage as sites endpoint may not exist
            localStorage.setItem('sites-data', JSON.stringify({
              data: newData,
              source,
              timestamp: new Date().toISOString()
            }));
            response = {
              id: `sites-${Date.now()}`,
              source,
              confidence: 'medium',
              data: newData,
              lastUpdated: new Date().toISOString()
            };
            break;
            
          default:
            throw new Error(`Unsupported data type: ${dataType}`);
        }
      }

      // Normalize response format - handle axios response structure
      const responseData = response.data || response; // axios puts data in .data property
      const normalizedResponse = {
        id: responseData.id || response.id || `${dataType}-${Date.now()}`,
        source: responseData.source || response.source || source,
        confidence: responseData.confidence || response.confidence || (source === 'user-entered' ? 'high' : 'medium'),
        data: responseData,
        lastUpdated: responseData.updated_at || responseData.lastUpdated || response.updated_at || response.lastUpdated || new Date().toISOString()
      };

      console.log(`[useDataManager] Normalized ${dataType} response:`, {
        id: normalizedResponse.id,
        source: normalizedResponse.source,
        dataKeys: Object.keys(normalizedResponse.data || {})
      });

      // Update local state - we need to track ID even in manual mode for subsequent updates
      setDataStates(prev => ({
        ...prev,
        [dataType]: {
          exists: true,
          source: normalizedResponse.source,
          confidence: normalizedResponse.confidence,
          data: normalizedResponse.data,
          lastUpdated: normalizedResponse.lastUpdated,
          id: normalizedResponse.id,
          degradedMode: false
        }
      }));

      return normalizedResponse;
    } catch (err) {
      setError(prev => ({ ...prev, [dataType]: 'Failed to save data' }));
      console.error(`Failed to save ${dataType} data:`, err);
      
      if (persistenceMode === 'manual') {
        // In manual mode, just throw the error - no localStorage fallback
        throw err;
      } else {
        // In auto mode, fallback to local storage in case of API failure
        const fallbackKey = `${dataType}-fallback`;
        localStorage.setItem(fallbackKey, JSON.stringify({
          data: newData,
          source: source,
          timestamp: new Date().toISOString()
        }));

        return null;
      }
    } finally {
      setLoading(prev => ({ ...prev, [dataType]: false }));
    }
  }, [persistenceMode]);

  /**
   * Mark data as complete from specific wizard
   */
  const markDataComplete = useCallback(async (dataType, source, finalData, explicitId = null) => {
    try {
      let response;
      
      // Route to appropriate completion endpoint
      switch (dataType) {
        case 'assessment':
          let assessmentId = explicitId || dataStates[dataType]?.id;
          
          // If no assessment ID exists, create the assessment first
          if (!assessmentId) {
            console.log('No assessment ID found, creating assessment first...');
            const createResponse = await saveUnifiedData(dataType, finalData, source, true);
            assessmentId = createResponse?.id;
            
            if (!assessmentId) {
              throw new Error('Failed to create assessment - no ID returned');
            }
          }
          
          // Now mark it as complete
          response = await apiClient.siteAssessment.markComplete(assessmentId, {
            status: 'COMPLETED',
            source: source,
            ...finalData
          });
          break;
          
        case 'journey':
          // Journey completion logic would go here
          response = { status: 'completed', data: finalData };
          break;
          
        default:
          response = { status: 'completed', data: finalData };
      }

      const currentState = dataStates[dataType];
      setDataStates(prev => ({
        ...prev,
        [dataType]: {
          ...prev[dataType],
          source: source,
          confidence: 'high',
          data: response.data || finalData,
          lastUpdated: response.lastUpdated || new Date().toISOString()
        }
      }));

      return response;
    } catch (err) {
      console.error(`Failed to mark ${dataType} complete:`, err);
      return null;
    }
  }, [dataStates]);

  /**
   * Get data summary for display in wizards
   */
  const getDataSummary = useCallback((dataType) => {
    const dataState = dataStates[dataType];
    if (!dataState?.exists) return null;

    const data = dataState.data;
    const sourceLabels = {
      'business-case': 'Business Case Wizard',
      'implementation': 'Implementation Wizard',
      'business-case-localStorage': 'Previous Business Case',
      'form-persistence-/business-case': 'Business Case (Auto-saved)',
      'form-persistence-/implementation': 'Implementation (Auto-saved)',
      'database-draft': 'Saved Draft',
      'api-enrichment': 'Company Data',
      'journey-context': 'Previous Session',
      'journey-localStorage': 'Previous Journey',
      'sites-localStorage': 'Saved Sites'
    };

    // Return type-specific summary
    switch (dataType) {
      case 'assessment':
        return {
          facility: data.facility_name || 'Unnamed Facility',
          size: data.total_area_sqm ? `${data.total_area_sqm?.toLocaleString()} sqm` : 'Size not specified',
          employees: data.employee_count ? `${data.employee_count} employees` : 'Employee count not specified',
          source: sourceLabels[dataState.source] || dataState.source,
          confidence: dataState.confidence,
          lastUpdated: dataState.lastUpdated,
          canEdit: true
        };
      case 'journey':
        return {
          stage: data.journeyStage || 'Unknown',
          progress: data.completedSteps?.length || 0,
          analyses: data.analysisHistory?.length || 0,
          source: sourceLabels[dataState.source] || dataState.source,
          confidence: dataState.confidence,
          lastUpdated: dataState.lastUpdated,
          canEdit: true
        };
      case 'sites':
        return {
          count: Array.isArray(data) ? data.length : 0,
          current: data.currentSite || 'None selected',
          source: sourceLabels[dataState.source] || dataState.source,
          confidence: dataState.confidence,
          lastUpdated: dataState.lastUpdated,
          canEdit: true
        };
      default:
        return {
          hasData: !!data,
          source: sourceLabels[dataState.source] || dataState.source,
          confidence: dataState.confidence,
          lastUpdated: dataState.lastUpdated,
          canEdit: true
        };
    }
  }, [dataStates]);

  /**
   * Clear data state (for testing or user choice)
   */
  const clearDataState = useCallback(async (dataType) => {
    try {
      const currentState = dataStates[dataType];
      
      // Route to appropriate deletion endpoint based on dataType
      if (currentState?.id) {
        switch (dataType) {
          case 'assessment':
            // Use site assessment deletion if available
            try {
              await apiClient.siteAssessment.delete(currentState.id);
            } catch (err) {
              console.warn('Could not delete from backend, clearing local state only:', err);
            }
            break;
            
          case 'journey':
            // Use journey deletion if available
            try {
              await apiClient.journey.delete(currentState.id);
            } catch (err) {
              console.warn('Could not delete from backend, clearing local state only:', err);
            }
            break;
            
          case 'sites':
            // Sites are local only, no backend deletion needed
            break;
            
          default:
            console.warn(`No deletion endpoint defined for dataType: ${dataType}`);
        }
      }
      
      setDataStates(prev => ({
        ...prev,
        [dataType]: {
          exists: false,
          source: null,
          confidence: null,
          data: null,
          lastUpdated: null,
          id: null,
          degradedMode: false
        }
      }));

      // Note: Journey context will read cleared state from getDataWithConfidence
      // No need for direct manipulation - data flows one way
      
      // Clear all potential localStorage sources
      clearLocalSources(dataType);

    } catch (err) {
      console.error(`Failed to clear ${dataType} state:`, err);
    }
  }, [dataStates]); // Removed circular dependency

  /**
   * Clear local sources after successful backend migration
   */
  const clearLocalSources = useCallback((dataType) => {
    switch (dataType) {
      case 'assessment':
        localStorage.removeItem('business-case-completed');
        localStorage.removeItem('site-assessment-fallback');
        ['/business-case', '/implementation'].forEach(route => {
          localStorage.removeItem(`motionlogic_form_${route}`);
        });
        break;
      case 'journey':
        localStorage.removeItem('motionlogic_journey_context');
        localStorage.removeItem('journey-fallback');
        break;
      case 'sites':
        const currentUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
        if (currentUser.id) {
          localStorage.removeItem(`user_sites_${currentUser.id}`);
          localStorage.removeItem(`current_site_${currentUser.id}`);
        }
        localStorage.removeItem('sites-fallback');
        break;
      default:
        localStorage.removeItem(`${dataType}-fallback`);
    }
  }, []);

  /**
   * Skip step if data exists (for wizard flow control)
   */
  const shouldSkipDataCollection = useCallback((dataType, minConfidence = 0.7) => {
    const dataState = dataStates[dataType];
    return dataState?.exists && dataState.confidence >= minConfidence;
  }, [dataStates]);

  // Enhanced delegation methods with intelligent source mapping
  const getAssessmentData = () => getDataWithConfidence('assessment');
  const saveAssessmentData = (data, source = 'user-direct', merge = true) => {
    // Map common sources to backend-recognized identifiers
    const sourceMapping = {
      'business-case': 'business-case-localStorage',
      'implementation': 'form-persistence-/implementation',
      'user-entered': 'user-direct',
      'form-auto-save': 'form-persistence-/business-case',
      'journey-context': 'journey-context'
    };
    
    const mappedSource = sourceMapping[source] || source;
    return saveUnifiedData('assessment', data, mappedSource, merge);
  };
  
  const getJourneyData = () => getDataWithConfidence('journey');
  const saveJourneyData = (data, source = 'user-direct', merge = true) => {
    const sourceMapping = {
      'journey-context': 'journey-context',
      'user-entered': 'user-direct',
      'localStorage': 'journey-localStorage'
    };
    
    const mappedSource = sourceMapping[source] || source;
    return saveUnifiedData('journey', data, mappedSource, merge);
  };
  
  const getSiteData = () => getDataWithConfidence('sites');
  const saveSiteData = (data, source) => saveUnifiedData('sites', data, source);

  return {
    // Generic methods
    checkUnifiedDataStatus,
    getDataWithConfidence,
    saveUnifiedData,
    markDataComplete,
    clearDataState,
    getDataSummary,
    shouldSkipDataCollection,
    
    // State access
    dataStates,
    loading,
    error,
    
    // Convenience methods for backwards compatibility
    getAssessmentData,
    saveAssessmentData,
    getJourneyData,
    saveJourneyData,
    getSiteData,
    saveSiteData,
    
    // Utilities
    collectLocalSources,
    clearLocalSources,
  };
};

export default useDataManager;
